"use client";

import styles from "@/styles/global/footer.module.scss";
import Image from "next/image";
import Link from "next/link";
import { useAppSelector } from "@/store/hooks";
import { usePathname } from "next/navigation";

interface SocialLinkType {
  title: string;
  href: string;
  src: string;
}

interface PaymentMethodType {
  title: string;
  src: string;
}

interface ExtraType {
  title: string;
  url: string;
}

export default function Footer() {
  const pathname = usePathname();
  const showFullFooter =
    !pathname.includes("/checkout") &&
    !pathname.includes("/payment-result/") &&
    !pathname.includes("/account/my-addresses") &&
    !pathname.includes("/account/create-address") &&
    !pathname.includes("/account/edit-address");

  // 从Redux store获取用户状态
  const { hasLogin } = useAppSelector((state) => state.user);

  const socialLinks = [
    {
      title: "Facebook",
      href: "https://www.facebook.com/CrazySales.com.au/",
      src: `//img.crazysales.com.au/images/newhomepage/mobile/svg/social/icon-social-fb-color.svg`,
    },
    {
      title: "YouTube",
      href: "https://www.youtube.com/@AUSWay",
      src: `//img.crazysales.com.au/images/newhomepage/mobile/svg/social/icon-social-ytb-color.svg`,
    },
    {
      title: "Twitter",
      href: "https://twitter.com/CrazySalesAus",
      src: `//img.crazysales.com.au/images/newhomepage/mobile/svg/social/icon-social-tw-color.svg`,
    },
    {
      title: "Instagram",
      href: "https://www.instagram.com/crazysalescomau/",
      src: `//img.crazysales.com.au/images/newhomepage/mobile/svg/social/icon-social-ins-color.svg`,
    },
    {
      title: "Pinterest",
      href: "https://au.pinterest.com/crazysales/",
      src: `//img.crazysales.com.au/images/newhomepage/mobile/svg/social/icon-social-pin-color.svg`,
    },
    {
      title: "Blog",
      href: "http://blog.crazysales.com.au/",
      src: `//img.crazysales.com.au/images/newhomepage/mobile/svg/social/icon-social-bg-color.svg`,
    },
  ];

  const paymentMethodList = [
    {
      title: "PayPal",
      src: `${process.env.IMAGE_DOMAIN}/svg/payment-method/icon-payment-logo-paypal.svg`,
    },
    {
      title: "Master",
      src: `${process.env.IMAGE_DOMAIN}/svg/payment-method/icon-payment-logo-master.svg`,
    },
    {
      title: "Visa",
      src: `${process.env.IMAGE_DOMAIN}/svg/payment-method/icon-payment-logo-visa.svg`,
    },
    {
      title: "Bpay",
      src: `${process.env.IMAGE_DOMAIN}/svg/payment-method/icon-payment-logo-bpay.svg`,
    },
    {
      title: "Amex",
      src: `${process.env.IMAGE_DOMAIN}/svg/payment-method/icon-payment-logo-amex.svg`,
    },
    {
      title: "Zippay",
      src: `${process.env.IMAGE_DOMAIN}/svg/payment-method/icon-payment-logo-zip.svg`,
    },
    {
      title: "Afterpay",
      src: `${process.env.IMAGE_DOMAIN}/svg/payment-method/icon-payment-logo-afterpay.svg`,
    },
  ];

  const extraList = [
    {
      title: "Terms & Use",
      url: "/article/TermsAndConditions",
    },
    {
      title: "Shipping Guide",
      url: "/deliverybrand",
    },
    {
      title: "Warranty Policy",
      url: "/article/TermsAndConditions#Warranty",
    },
    {
      title: "About US",
      url: "/article/AboutUs",
    },
    {
      title: "Privacy Policy",
      url: "/article/PrivacyStatement",
    },
    {
      title: "Return And Refund Policy",
      url: "/article/CrazysalesReturnPolicy",
    },
    {
      title: "Contact US",
      url: "/contact",
    },
    {
      title: "Blogger Cooperation Program",
      url: "/article/blogger_cooperation",
    },
    {
      title: "Newsletter",
      url: "/newsletters/index",
    },
    {
      title: "Crazy Sales reviews",
      url: "/review/",
    },
    {
      title: "Afterpay",
      url: "/afterpay",
    },
    {
      title: "Zippay",
      url: "/zippay",
    }
  ];

  return (
    <div className={styles["comp-footer"]}>
      {showFullFooter ? (
        <>
          <div className={styles["social-media-box"]}>
            <Link
              key={"thread"}
              href={"https://www.threads.net/@crazysalescomau"}
            >
              <Image
                className={styles["social-media-item-logo"]}
                src={`${process.env.IMAGE_DOMAIN}/home/threads-icon.png`}
                alt={"thread"}
                width={35}
                height={35}
              />
            </Link>
            {socialLinks.map((social: SocialLinkType) => (
              <Link key={social.title} href={social.href}>
                <Image
                  className={styles["social-media-item"]}
                  src={social.src}
                  alt={social.title}
                  width={40}
                  height={40}
                />
              </Link>
            ))}
          </div>

          <Link href={"/category"} className={styles["all-categories"]}>
            All Categories
          </Link>

          <div className={styles["accept-title"]}>We proudly accept:</div>

          <div className={styles["accept-payment-list"]}>
            {paymentMethodList.map((payment: PaymentMethodType) => (
              <Image
                className={styles["accept-payment-item"]}
                key={payment.src}
                src={payment.src}
                alt={payment.title}
                width={47}
                height={27}
              />
            ))}
          </div>

          {!hasLogin ? (
            <div className={styles["login-tips"]}>
              Please
              <Link href={"/login"}>
                <span className={styles["login-link"]}>Sign in</span>
              </Link>
              if you are already a customer.
            </div>
          ) : null}

          <Link href={"https://www.crazysales.com.au/?fullsite=1"}>
            <div className={styles["full-site"]}>Go to Full Site</div>
          </Link>

          <div className={styles["extra-site-list"]}>
            {extraList.map((extra: ExtraType) => (
              <Link
                className={styles["extra-site-item"]}
                href={extra.url}
                key={extra.url}
              >
                {extra.title}
              </Link>
            ))}
          </div>
        </>
      ) : null}

      <div
        className={styles["copy-right"]}
        style={
          pathname == "/checkout" ||
          pathname.includes("/online-") ||
          pathname.includes("/view-cart")
            ? { paddingBottom: "90px" }
            : { paddingBottom: "24px" }
        }
      >
        Copyright &copy; 2007 - 2026 Crazy Sales. All rights reserved
      </div>
    </div>
  );
}
